import { useEffect } from 'react';

interface KeyboardShortcutOptions {
  /** Key to listen for. Compared against `event.key` (case-sensitive). */
  key: string;
  /** Handler invoked when the key is pressed and `enabled` allows it. */
  handler: (event: KeyboardEvent) => void;
  /**
   * If `true` (default), the shortcut is suppressed when the user is currently
   * typing in an `input`, `textarea` or `contenteditable` element. Set to
   * `false` for shortcuts that should fire even while typing (e.g. `Escape`
   * to clear the search box).
   */
  ignoreWhenTyping?: boolean;
  /**
   * If `true`, calls `event.preventDefault()` after invoking the handler.
   * Default `true` so the browser's default action for the key (e.g. `/`
   * triggering quick-find in some browsers) is suppressed.
   */
  preventDefault?: boolean;
  /** Set to `false` to disable the shortcut without unmounting. Default `true`. */
  enabled?: boolean;
}

const isTypingInElement = (target: EventTarget | null): boolean => {
  if (!target || !(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
};

/**
 * Attach a single keyboard shortcut to `document`. Cleans up on unmount and
 * re-binds when its dependencies change.
 *
 * Usage:
 * ```tsx
 * useKeyboardShortcut({
 *   key: '/',
 *   handler: () => searchInputRef.current?.focus(),
 * });
 * ```
 */
export const useKeyboardShortcut = ({
  key,
  handler,
  ignoreWhenTyping = true,
  preventDefault = true,
  enabled = true,
}: KeyboardShortcutOptions): void => {
  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== key) return;
      // Skip when modifier keys are pressed — these shortcuts are meant to
      // be unmodified single keys, not chords.
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (ignoreWhenTyping && isTypingInElement(event.target)) return;
      if (preventDefault) event.preventDefault();
      handler(event);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [key, handler, ignoreWhenTyping, preventDefault, enabled]);
};
